import { claimflowApiClient } from './claimflowApiClient';
import { parseReceivedEmail } from '../components/documents/emailParser';

/**
 * Email composer API service.
 *
 * Every call goes through origami's generic ERP proxy
 * (`/claimflow/erp/registries/veos/endpoints/{slug}`), which wraps the
 * `ErpProxyResponse<T>` envelope around the raw VEOS payload. GET inputs are
 * passed as query params; the `mail-send` POST sends its inputs in the request
 * body under `data` (the proxy merges `query()` + `input('data')` before
 * routing to VEOS).
 *
 * See `.claude-work/specs/claimflow-email/SPEC.md` (mahhp 21637) for the full
 * contract, slugs, and response shapes.
 */

interface ErpProxyResponse<T> {
  success: boolean;
  http_status?: number;
  data: T;
  error?: { code: string; message: string } | null;
}

type ErpListResponse<T> = ErpProxyResponse<{
  total?: number;
  // VEOS may omit `data` on an empty result — keep it optional so the
  // `?? []` fallback at the call sites is type-sound.
  data?: T[] | null;
}>;

// ---------------------------------------------------------------------------
// Raw VEOS row shapes
// ---------------------------------------------------------------------------

/** A row from `WS2_GS_SEL_IDCMAIL` (email templates). */
interface VeosTemplateRow {
  ID_CMAIL: number | string;
  SUJET_CMAIL: string;
}

/** A row from `WS2_GS_SEL_EMAIL_RECU` (received emails to reply to). */
interface VeosReplyToRow {
  ID_ADOC: number | string;
  LIB_ADOC: string;
}

/** A row from `WS2_GS_SEL_EMAIL` (recipient suggestions). */
interface VeosRecipientRow {
  // VEOS may return null/empty addresses — keep nullable so the `?.trim()` +
  // filter below is type-sound and survives bad data.
  EMAIL: string | null;
}

/** A row from `WS2_GS_SEL_IDADOC` (GED documents to attach). */
interface VeosAttachableRow {
  ID_ADOC: number | string;
  LIB_ADOC: string;
}

/** A row from `WS2_GS_SEL_EXPEDITEUR` (the sender / "De" prepopulation). */
interface VeosSenderRow {
  EXPEDITEUR_EMAIL: string;
  EXPEDITEUR_NOM: string;
}

/** Response of `mail-fusion-get` (a preloaded template). */
interface VeosTemplateContent {
  id?: number | string;
  titre?: string;
  data?: string;
  categ?: string;
  confid?: string;
}

/** Subset of `document-get` we read for the reply-to preload. */
interface VeosDocumentContent {
  label?: string;
  name?: string;
  contentType?: string;
  /** Base64 of the file — parsed in-browser when the document is an email. */
  datas?: string;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Exported (mapped) shapes
// ---------------------------------------------------------------------------

export interface EmailTemplate {
  id: string;
  label: string;
}

export interface ReplyToOption {
  id: string;
  label: string;
}

export interface AttachableDocument {
  id: string;
  label: string;
}

/** The default sender ("De") — email + display name from `WS2_GS_SEL_EXPEDITEUR`. */
export interface Sender {
  email: string;
  name: string;
}

/**
 * An original (received) email preloaded for "En réponse à". `label`/`name`/
 * `contentType` are the document metadata; `subject`/`body`/`fromAddress` are
 * filled when the document is a parseable email (else empty — the caller seeds
 * from the label).
 */
export interface OriginalDocument {
  label: string;
  name: string;
  contentType: string;
  /** Origin subject parsed from the email, or '' when not an email. */
  subject: string;
  /** Origin body (HTML when `bodyIsHtml`, else plain text), or '' when unparseable. */
  body: string;
  /** True when `body` is HTML; false/undefined when it is plain text. */
  bodyIsHtml: boolean;
  /**
   * The origin sender's bare address, or '' when not parsed (mahhp 22859).
   * Not validated here (may be an Exchange DN) — the caller checks it before
   * preloading it into "À".
   */
  fromAddress: string;
}

export interface SendEmailInput {
  idSin: string | number;
  from: string;
  senderName?: string;
  to: string[];
  cc: string[];
  subject: string;
  /** Message body as an HTML string (from the RichTextEditor); sent as-is. */
  body: string;
  attachmentIds: string[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function unwrap<T>(envelope: ErpProxyResponse<T>): T {
  if (!envelope.success) {
    throw new Error(envelope.error?.message ?? 'ERP request failed');
  }
  return envelope.data;
}

const ENDPOINT_BASE = '/claimflow/erp/registries/veos/endpoints';

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const emailService = {
  /** Email templates list — "Utiliser un modèle" (`WS2_GS_SEL_IDCMAIL`). */
  async fetchTemplates(): Promise<EmailTemplate[]> {
    const response = await claimflowApiClient.get<ErpListResponse<VeosTemplateRow>>(
      `${ENDPOINT_BASE}/boby-ws2-gs-sel-idcmail`,
      { params: { perPage: 9999 } },
    );
    const rows = unwrap(response.data).data ?? [];
    return rows.map((row) => ({
      id: String(row.ID_CMAIL),
      label: row.SUJET_CMAIL,
    }));
  },

  /** Received emails to reply to — "En réponse à" (`WS2_GS_SEL_EMAIL_RECU`). */
  async fetchReplyToOptions(idSin: string | number): Promise<ReplyToOption[]> {
    const response = await claimflowApiClient.get<ErpListResponse<VeosReplyToRow>>(
      `${ENDPOINT_BASE}/boby-ws2-gs-sel-email-recu`,
      { params: { id_sin: idSin, perPage: 9999 } },
    );
    const rows = unwrap(response.data).data ?? [];
    return rows.map((row) => ({
      id: String(row.ID_ADOC),
      label: row.LIB_ADOC,
    }));
  },

  /** Recipient address suggestions for À / CC (`WS2_GS_SEL_EMAIL`). */
  async fetchRecipientSuggestions(idSin: string | number): Promise<string[]> {
    const response = await claimflowApiClient.get<ErpListResponse<VeosRecipientRow>>(
      `${ENDPOINT_BASE}/boby-ws2-gs-sel-email`,
      { params: { id_sin: idSin, perPage: 9999 } },
    );
    const rows = unwrap(response.data).data ?? [];
    return rows
      .map((row) => row.EMAIL?.trim())
      .filter((email): email is string => Boolean(email));
  },

  /**
   * Default sender ("De") — first row of `WS2_GS_SEL_EXPEDITEUR`. Returns the
   * mapped `{ email, name }` or `null` when VEOS returns no rows.
   */
  async fetchSender(): Promise<Sender | null> {
    const response = await claimflowApiClient.get<ErpListResponse<VeosSenderRow>>(
      `${ENDPOINT_BASE}/boby-ws2-gs-sel-expediteur`,
    );
    const rows = unwrap(response.data).data ?? [];
    const first = rows[0];
    if (!first) return null;
    return {
      email: first.EXPEDITEUR_EMAIL,
      name: first.EXPEDITEUR_NOM,
    };
  },

  /** GED documents available to attach — "PJ" (`WS2_GS_SEL_IDADOC`). */
  async fetchAttachableDocuments(idSin: string | number): Promise<AttachableDocument[]> {
    const response = await claimflowApiClient.get<ErpListResponse<VeosAttachableRow>>(
      `${ENDPOINT_BASE}/boby-ws2-gs-sel-idadoc`,
      { params: { id_sin: idSin, perPage: 9999 } },
    );
    const rows = unwrap(response.data).data ?? [];
    return rows.map((row) => ({
      id: String(row.ID_ADOC),
      label: row.LIB_ADOC,
    }));
  },

  /** Preload a template's subject + body (`mail-fusion-get`). */
  async fetchTemplateContent(
    idCmail: string | number,
    idSin: string | number,
  ): Promise<{ subject: string; body: string }> {
    const response = await claimflowApiClient.get<ErpProxyResponse<VeosTemplateContent>>(
      `${ENDPOINT_BASE}/mail-fusion-get`,
      { params: { idCmail, entity: idSin } },
    );
    const content = unwrap(response.data);
    return {
      subject: content.titre ?? '',
      body: content.data ?? '',
    };
  },

  /**
   * Preload an original (received) email document when a reply-to option is
   * selected (`document-get`). Alongside the metadata (label/name/contentType)
   * we parse the `datas` base64 with the shared received-email parser to extract
   * the origin **subject**, **body** and **sender address** for the reply
   * prefill. Parsing fails soft: a PDF (or any non-email/unparseable file)
   * yields empty subject/body/fromAddress, so the caller falls back to seeding
   * from the dropdown label.
   */
  async fetchOriginalDocument(idAdoc: string | number): Promise<OriginalDocument> {
    const response = await claimflowApiClient.get<ErpProxyResponse<VeosDocumentContent>>(
      `${ENDPOINT_BASE}/document-get`,
      { params: { id: idAdoc } },
    );
    const doc = unwrap(response.data);
    const label = doc.label ?? '';
    const name = doc.name ?? '';
    const contentType = doc.contentType ?? '';

    // No bytes, or a content type that isn't a received email → metadata only.
    if (!doc.datas) {
      return {
        label,
        name,
        contentType,
        subject: '',
        body: '',
        bodyIsHtml: false,
        fromAddress: '',
      };
    }

    // `bestEffort` is set when the parser could not produce a real email view
    // (e.g. a PDF, or a parse failure) — in that case the body is empty and we
    // keep subject/body blank so the caller seeds from the label instead.
    const parsed = await parseReceivedEmail(doc.datas, contentType, name, label);
    if (parsed.bestEffort) {
      return {
        label,
        name,
        contentType,
        subject: '',
        body: '',
        bodyIsHtml: false,
        fromAddress: '',
      };
    }
    return {
      label,
      name,
      contentType,
      subject: parsed.email.subject,
      body: parsed.email.body,
      bodyIsHtml: parsed.email.bodyIsHtml ?? false,
      fromAddress: parsed.fromAddress,
    };
  },

  /** Send the composed email (`mail-send`, POST). */
  async sendEmail(input: SendEmailInput): Promise<void> {
    const params = {
      type: 'simple',
      service: 'SINISTRE',
      entite: String(input.idSin),
      confid: '0',
      destinataires: input.to.map((mail) => ({ mail })),
      destinatairescc: input.cc.map((mail) => ({ mail })),
      expediteur: { mail: input.from, nom: input.senderName ?? '' },
      pj: input.attachmentIds.length
        ? [{ type: 'ged', idArchivdoc: input.attachmentIds.map(String) }]
        : [],
      objet: input.subject,
      // `body` is already HTML (produced by the RichTextEditor / a Fusion
      // template), so it passes through unchanged. An empty editor yields
      // `<p></p>`.
      message: input.body.trim().length === 0 ? '<p></p>' : input.body,
    };

    const response = await claimflowApiClient.post<ErpProxyResponse<unknown>>(
      `${ENDPOINT_BASE}/mail-send`,
      { data: { requests: [{ params }] } },
    );
    unwrap(response.data);
  },
};
